// Minimal EXIF reader for the photo quick-fill: where and when was this JPEG
// taken. Hand-written against the TIFF/EXIF byte layout — no library, same
// zero-dependency rule as the rest of the repo — and parsed entirely in the
// browser: the photo itself never leaves the device.
//
// Scope is deliberately narrow: GPSLatitude/GPSLongitude and
// DateTimeOriginal (plus fallbacks) out of the first APP1 segment of a JPEG.
// Anything else — HEIC containers, XMP, maker notes — is out; a photo the
// parser can't read simply doesn't prefill the form.
//
// Lives in src/lib/ so the client bundle and the Node test
// (src/lib/exif.test.js) share one copy — the variables.ts trick.

// How much of the file the caller needs to hand over: EXIF must precede the
// scan data and an APP1 payload caps at 64 KB, so 256 KB covers even files
// with oversized preceding segments.
export const EXIF_SCAN_BYTES = 256 * 1024;

// What one photo yielded.
export interface ExifResult {
  /** True when a JPEG with an EXIF block was read — fields may still be null. */
  ok: boolean;
  reason: null | 'not-jpeg' | 'no-exif';
  /** Signed decimal degrees. */
  lat: number | null;
  /** Signed decimal degrees. */
  lon: number | null;
  /** From DateTimeOriginal — camera-local, not UTC. */
  date: { y: number; m: number; d: number } | null;
  /** Fractional local hour, 0..24. */
  hour: number | null;
}

// One IFD entry, as readIFD records it.
interface IfdEntry {
  type: number;
  count: number;
  at: number;
}

// Never throws: a truncated or malicious buffer yields whatever fields were
// readable before the damage.
export function parseExif(buf: ArrayBuffer): ExifResult {
  const out: ExifResult = { ok: false, reason: 'not-jpeg', lat: null, lon: null, date: null, hour: null };
  try {
    const view = new DataView(buf);
    if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return out; // SOI

    // Walk JPEG segments to the first APP1 whose payload starts "Exif\0\0".
    let off = 2;
    let app1 = -1;
    while (off + 4 <= view.byteLength) {
      if (view.getUint8(off) !== 0xff) break;
      const marker = view.getUint8(off + 1);
      if (marker === 0xda) break; // SOS — scan data, EXIF can't follow
      const len = view.getUint16(off + 2); // includes the 2 length bytes
      if (len < 2) break;
      if (marker === 0xe1 && off + 10 <= view.byteLength && view.getUint32(off + 4) === 0x45786966 /* 'Exif' */ && view.getUint16(off + 8) === 0) {
        app1 = off + 10; // TIFF header starts here
        break;
      }
      off += 2 + len;
    }
    if (app1 < 0) {
      out.reason = 'no-exif';
      return out;
    }

    // TIFF header: byte order, magic 42, offset of IFD0. Every offset below is
    // relative to this header start.
    const bo = view.getUint16(app1);
    const le = bo === 0x4949; // 'II'
    if (!le && bo !== 0x4d4d) return out; // neither II nor MM
    if (view.getUint16(app1 + 2, le) !== 42) return out;
    out.ok = true;
    out.reason = null;

    const u16 = (o: number) => view.getUint16(app1 + o, le);
    const u32 = (o: number) => view.getUint32(app1 + o, le);

    // One IFD -> Map(tag -> {type, count, at}), where `at` is the offset (from
    // the TIFF header) of the value bytes: inline when they fit in 4 bytes,
    // pointed-to otherwise.
    const TYPE_SIZE: Record<number, number> = { 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 7: 1, 9: 4, 10: 8 };
    function readIFD(dirOff: number): Map<number, IfdEntry> {
      const map = new Map<number, IfdEntry>();
      if (dirOff <= 0 || app1 + dirOff + 2 > view.byteLength) return map;
      const n = u16(dirOff);
      for (let i = 0; i < n; i++) {
        const e = dirOff + 2 + i * 12;
        if (app1 + e + 12 > view.byteLength) break;
        const tag = u16(e);
        const type = u16(e + 2);
        const count = u32(e + 4);
        const size = (TYPE_SIZE[type] || 0) * count;
        const at = size > 4 ? u32(e + 8) : e + 8;
        if (size === 0 || app1 + at + size > view.byteLength) continue;
        map.set(tag, { type, count, at });
      }
      return map;
    }

    const ascii = (v: IfdEntry | undefined): string | null => {
      if (!v || v.type !== 2) return null;
      let s = '';
      for (let i = 0; i < v.count; i++) {
        const c = view.getUint8(app1 + v.at + i);
        if (c === 0) break;
        s += String.fromCharCode(c);
      }
      return s;
    };
    // Unsigned RATIONAL[i]; NaN on a zero denominator.
    const rational = (v: IfdEntry | undefined, i: number): number => {
      if (!v || v.type !== 5 || i >= v.count) return NaN;
      const num = u32(v.at + i * 8);
      const den = u32(v.at + i * 8 + 4);
      return den === 0 ? NaN : num / den;
    };

    const ifd0 = readIFD(u32(4));
    const exifIfd = ifd0.has(0x8769) ? readIFD(u32(ifd0.get(0x8769)!.at)) : new Map<number, IfdEntry>();
    const gpsIfd = ifd0.has(0x8825) ? readIFD(u32(ifd0.get(0x8825)!.at)) : new Map<number, IfdEntry>();

    // --- when ------------------------------------------------------------------
    // DateTimeOriginal (the shutter moment) > DateTimeDigitized > IFD0 DateTime
    // (file-modified — last resort). Camera-local wall clock, "YYYY:MM:DD HH:MM:SS".
    const dateStr = ascii(exifIfd.get(0x9003)) || ascii(exifIfd.get(0x9004)) || ascii(ifd0.get(0x0132));
    if (dateStr) {
      const m = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})/.exec(dateStr);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]);
        const d = Number(m[3]);
        const hh = Number(m[4]);
        const mi = Number(m[5]);
        const now = new Date().getFullYear();
        if (y >= 1990 && y <= now + 1 && mo >= 1 && mo <= 12 && d >= 1 && d <= 31) {
          out.date = { y, m: mo, d };
          // Camera wall clock vs the data model's local *solar* buckets: civil
          // time skews from solar by ≤ ~1.5 h nearly everywhere, well inside a
          // bucket's 3 h window — so the clock hour feeds bucketForHour as-is.
          if (hh <= 24 && mi < 60) out.hour = hh + mi / 60;
        }
      }
    }

    // --- where -----------------------------------------------------------------
    // Lat/lon are 3 RATIONALs (deg, min, sec); the N/S/E/W refs carry the sign.
    const dms = (v: IfdEntry | undefined): number => rational(v, 0) + rational(v, 1) / 60 + rational(v, 2) / 3600;
    const latRef = ascii(gpsIfd.get(0x0001));
    const lonRef = ascii(gpsIfd.get(0x0003));
    if (gpsIfd.has(0x0002) && gpsIfd.has(0x0004)) {
      let lat = dms(gpsIfd.get(0x0002));
      let lon = dms(gpsIfd.get(0x0004));
      if (latRef === 'S') lat = -lat;
      if (lonRef === 'W') lon = -lon;
      // Reject the unusable: non-finite, out of range, and the (0,0) null
      // island some phones write when the fix failed.
      if (Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180 && !(lat === 0 && lon === 0)) {
        out.lat = lat;
        out.lon = lon;
      }
    }

    return out;
  } catch {
    return out;
  }
}
